import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import {
  useProgressiveCursorList as useCursorPagination,
  type ProgressiveCursorListResult as CursorPaginationResult,
} from "../../hooks/useProgressiveCursorList.js";
import * as api from "../../api/teams-api.js";

/**
 * Server state for the shared-Teams screens (ADR-0082).
 *
 * ONE invalidation rule, stated once here rather than at each call site: every
 * mutation invalidates the whole `teams` key. That is deliberately coarse — a
 * roster change moves `memberCount` on a card the list is showing, an
 * acceptance moves a row out of "Requests" and into "My teams", and an archive
 * moves a team out of Explore. Invalidating precisely would mean enumerating
 * those couplings at eleven call sites and getting one of them wrong.
 *
 * There is NO optimistic update anywhere in this file, and that is the same
 * decision as the version preconditions on the server: the screens are about
 * consent and ownership, where showing an outcome that has not happened is
 * worse than a short wait. A pending invitation rendered as a membership is the
 * exact failure the whole model exists to prevent.
 *
 * EVERY LIST IS PAGINATED, through the shell's shared `useCursorPagination`.
 * All four read endpoints have taken a `cursor` since they shipped, and these
 * hooks used to call the first page and stop — which is not a smaller list, it
 * is a WRONG one: a roster is bounded at 1000 people and served 100 at a time,
 * so the eleventh page of a large team was simply invisible, and so were the
 * per-member owner controls on it. A cap that nobody can see past is worse than
 * a page control, because it looks complete.
 *
 * THE QUERY KEY CARRIES EVERY SERVER-SIDE FILTER, which is what makes a search
 * reset rather than append: typing in the box mints a new key, so page 2 of
 * "quality" can never be concatenated onto page 1 of "qual".
 */

const ROOT = ["teams"] as const;

export const teamKeys = {
  list: (scope: "mine" | "explore", query: string) =>
    [...ROOT, "list", scope, query] as const,
  detail: (teamId: string) => [...ROOT, "detail", teamId] as const,
  members: (teamId: string) => [...ROOT, "members", teamId] as const,
  myRequests: (status: api.TeamRequestStatus) =>
    [...ROOT, "requests", "mine", status] as const,
  incomingRequests: (teamId?: string) =>
    [...ROOT, "requests", "incoming", teamId ?? "all"] as const,
};

export function useTeamList(
  scope: "mine" | "explore",
  query: string,
): CursorPaginationResult<api.Team> {
  return useCursorPagination<api.Team>({
    queryKey: teamKeys.list(scope, query),
    fetchPage: (cursor) => api.listTeams({ scope, query, cursor }),
    /* The key changes as the reader types, so without this the table empties
       and refills on every letter. */
    keepPreviousWhileLoading: true,
  });
}

export function useTeam(teamId: string): UseQueryResult<api.Team> {
  return useQuery({
    queryKey: teamKeys.detail(teamId),
    queryFn: () => api.getTeam(teamId),
  });
}

export function useTeamMembers(
  teamId: string,
): CursorPaginationResult<api.TeamMember> {
  return useCursorPagination<api.TeamMember>({
    queryKey: teamKeys.members(teamId),
    fetchPage: (cursor) => api.listTeamMembers(teamId, { cursor }),
    enabled: teamId !== "",
  });
}

export function useMyTeamRequests(
  status: api.TeamRequestStatus = "pending",
): CursorPaginationResult<api.TeamRequest> {
  return useCursorPagination<api.TeamRequest>({
    queryKey: teamKeys.myRequests(status),
    fetchPage: (cursor) => api.listMyTeamRequests({ status, cursor }),
  });
}

/**
 * The owner's queue.
 *
 * `teamId` narrows it SERVER-SIDE. The team page used to read the whole queue
 * and filter it in the browser, which is wrong twice over: it is the caller's
 * every team rather than this one, and a team whose requests sit past the first
 * page of that combined queue showed none at all.
 */
export function useIncomingTeamRequests(
  options: { teamId?: string } = {},
): CursorPaginationResult<api.TeamRequest> {
  const teamId = options.teamId;
  return useCursorPagination<api.TeamRequest>({
    queryKey: teamKeys.incomingRequests(teamId),
    fetchPage: (cursor) =>
      api.listIncomingTeamRequests({ ...(teamId ? { teamId } : {}), cursor }),
  });
}

/** Every mutation below goes through this, so none of them can forget to
 *  invalidate — see the module comment for why the key is the whole family. */
function useTeamMutation<TInput, TResult>(
  mutationFn: (input: TInput) => Promise<TResult>,
): UseMutationResult<TResult, Error, TInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ROOT });
    },
  });
}

export function useCreateTeam() {
  return useTeamMutation(api.createTeam);
}

export function useUpdateTeam(teamId: string) {
  return useTeamMutation(
    (input: { name: string; description: string | null; version: number }) =>
      api.updateTeam(teamId, input),
  );
}

export function useArchiveTeam(teamId: string) {
  return useTeamMutation((version: number) => api.archiveTeam(teamId, version));
}

export function useInviteToTeam(teamId: string) {
  return useTeamMutation((userId: string) => api.inviteToTeam(teamId, userId));
}

export function useRequestToJoinTeam() {
  return useTeamMutation(api.requestToJoinTeam);
}

export function useLeaveTeam() {
  return useTeamMutation(api.leaveTeam);
}

export function useSetTeamMemberRole(teamId: string) {
  return useTeamMutation(
    (input: { userId: string; role: api.TeamMemberRole; version: number }) =>
      api.setTeamMemberRole(teamId, input.userId, {
        role: input.role,
        version: input.version,
      }),
  );
}

export function useRemoveTeamMember(teamId: string) {
  return useTeamMutation((input: { userId: string; version: number }) =>
    api.removeTeamMember(teamId, input.userId, input.version),
  );
}

/**
 * The two ADMINISTRATOR powers (ADR-0082 rule 7).
 *
 * Neither adds anybody to a team: recovery PROMOTES somebody already on it, and
 * archiving retires a team nobody is left to own. Both are tenant-admin gated
 * on the server, and both carry a required, audited reason — an out-of-band act
 * has to explain itself afterwards.
 */
export function useRecoverTeamOwnership(teamId: string) {
  return useTeamMutation((input: { userId: string; reason: string }) =>
    api.recoverTeamOwnership(teamId, input),
  );
}

export function useArchiveOwnerlessTeam(teamId: string) {
  return useTeamMutation((input: { reason: string }) =>
    api.archiveOwnerlessTeam(teamId, input),
  );
}

export function useResolveTeamRequest() {
  return useTeamMutation(
    (input: { requestId: string; action: api.TeamRequestAction }) =>
      api.resolveTeamRequest(input.requestId, input.action),
  );
}
